import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from '../../utils/translations';
import supabase from '../../lib/supabase';
import { v4 as uuidv4 } from 'uuid';
import { fetchActiveAgents } from '../../utils/agentUtils';
import Modal from "../Common/Modal"
import { toast } from "sonner"
import Button from "../Common/Button"

const { FiPlus, FiEdit2, FiTrash2, FiUsers, FiUserPlus, FiSave, FiX, FiCheck, FiMail, FiUser, FiLoader, FiShield, FiEdit3, FiRefreshCw, FiUserX, FiSearch } = FiIcons;

const TeamManagementPage = ({ setActiveTab }) => {
  const { user, refreshUserProfile, updateUserRole } = useAuth();
  const t = useTranslation(user?.language || 'fr'); // Default to French
  const [teams, setTeams] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isCreatingTeam, setIsCreatingTeam] = useState(false);
  const [isInvitingUser, setIsInvitingUser] = useState(false);
  const [isEditingUser, setIsEditingUser] = useState(false);
  const [isAddingMember, setIsAddingMember] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [invitationLoading, setInvitationLoading] = useState(false);
  const [userUpdateLoading, setUserUpdateLoading] = useState(false);
  const [refreshingData, setRefreshingData] = useState(false);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [availableAgents, setAvailableAgents] = useState([]);
  const [teamForm, setTeamForm] = useState({
    name: '',
    description: '',
    allowedAgents: []
  });
  const [inviteForm, setInviteForm] = useState({
    fullName: '',
    email: '',
    role: 'Team Member',
    teamIds: []
  });
  const [userEditForm, setUserEditForm] = useState({
    fullName: '',
    email: '',
    role: 'Team Member',
    teamIds: []
  });
  const [teamMemberIds, setTeamMemberIds] = useState([]); // New state for team member IDs

  const isAdmin = user?.role === 'Administrator';
  console.log('TeamManagementPage - User role:', user?.role, 'isAdmin:', isAdmin);

  const generateUniqueInvitationId = async () => {
    let isUnique = false;
    let invitationId = '';
    let attempts = 0;
    const maxAttempts = 10;

    while (!isUnique && attempts < maxAttempts) {
      invitationId = uuidv4();
      attempts++;

      try {
        const { data: existingProfile, error: profileError } = await supabase
          .from('user_profiles_a1b2c3d4e5')
          .select('id')
          .eq('id', invitationId)
          .maybeSingle();

        if (profileError && profileError.code !== 'PGRST116') {
          console.error('Error checking profile uniqueness:', profileError);
          continue;
        }

        const { data: existingMember, error: memberError } = await supabase
          .from('team_members_a1b2c3d4e5')
          .select('user_id')
          .eq('user_id', invitationId)
          .maybeSingle();

        if (memberError && memberError.code !== 'PGRST116') {
          console.error('Error checking member uniqueness:', memberError);
          continue;
        }

        const { data: existingTeam, error: teamError } = await supabase
          .from('teams_a1b2c3d4e5')
          .select('id')
          .eq('id', invitationId)
          .maybeSingle();

        if (teamError && teamError.code !== 'PGRST116') {
          console.error('Error checking team uniqueness:', teamError);
          continue;
        }

        if (!existingProfile && !existingMember && !existingTeam) {
          isUnique = true;
        }
      } catch (error) {
        console.error('Error during UUID uniqueness check:', error);
      }
    }

    if (!isUnique) {
      throw new Error(t('teamManagement.invitationIdError', 'Failed to generate unique invitation ID after multiple attempts'));
    }

    return invitationId;
  };

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const { data: teamsData, error: teamsError } = await supabase
        .from('teams_a1b2c3d4e5')
        .select('*');

      if (teamsError) throw teamsError;
      setTeams(teamsData || []);

      if (isAdmin) {
        console.log('Fetching all users for admin...');
        const { data: allUsersData, error: allUsersError } = await supabase
          .from('user_profiles_a1b2c3d4e5')
          .select('*')
          .order('full_name');

        if (allUsersError) throw allUsersError;
        setAllUsers(allUsersData || []);
        console.log('Fetched', allUsersData?.length || 0, 'users');
      }
      
      const agents = await fetchActiveAgents(null, isAdmin);
      setAvailableAgents(agents);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error(t('teamManagement.loadDataError', 'Error loading data'));
    } finally {
      setIsLoading(false);
    }
  };  

  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [isAdmin, user]);

  useEffect(() => {
    if (!user) return;
    
    const channel = supabase
      .channel('public:ai_agents_changes')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'ai_agents_a1b2c3d4e5' },
        async () => {
          console.log('Agents table changed, refreshing available agents');
          const agents = await fetchActiveAgents(null, isAdmin);
          setAvailableAgents(agents);
        }
      )
      .subscribe();
      
    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, isAdmin]);

  useEffect(() => {
    const fetchTeamMembers = async () => {
      if (!selectedTeam) {
        setTeamMemberIds([]);
        return;
      }

      try {
        const { data: memberships, error: membershipError } = await supabase
          .from('team_members_a1b2c3d4e5')
          .select('user_id')
          .eq('team_id', selectedTeam.id);

        if (membershipError) throw membershipError;
        setTeamMemberIds(memberships?.map(m => m.user_id) || []);
      } catch (error) {
        console.error('Error fetching team members:', error);
        toast.error(t('teamManagement.loadTeamMembersError', 'Error loading team members'));
      }
    };

    fetchTeamMembers();
  }, [selectedTeam]);

  const handleSelectTeam = async (team) => {
    setSelectedTeam(team);
    try {
      const { data: allowedAgents, error } = await supabase
        .from('team_allowed_agents_a1b2c3d4e5')
        .select('agent_id')
        .eq('team_id', team.id);

      if (error) throw error;

      setTeamForm({
        name: team.name,
        description: team.description || '',
        allowedAgents: allowedAgents?.map(a => a.agent_id) || []
      });
    } catch (error) {
      console.error('Error fetching team details:', error);
      toast.error(t('teamManagement.loadTeamDetailsError', 'Error loading team details'));
    }
  };

  const handleSelectUser = async (selectedUser) => {
    setIsCreatingTeam(false);
    setIsEditingUser(true);
    setSelectedUser(selectedUser);
    try {
      const { data: userTeams, error: teamsError } = await supabase
        .from('team_members_a1b2c3d4e5')
        .select('team_id')
        .eq('user_id', selectedUser.id);

      if (teamsError) throw teamsError;

      setUserEditForm({
        fullName: selectedUser.full_name || '',
        email: selectedUser.email || '',
        role: selectedUser.role || 'Team Member',
        teamIds: userTeams?.map(t => t.team_id) || []
      });
    } catch (error) {
      console.error('Error fetching user details:', error);
      toast.error(t('teamManagement.loadUserDetailsError', 'Error loading user details'));
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    if (!selectedUser) return;

    setUserUpdateLoading(true);

    try {
      const isRoleChanged = userEditForm.role !== selectedUser.role;

      const { error: profileError } = await supabase
        .from('user_profiles_a1b2c3d4e5')
        .update({
          full_name: userEditForm.fullName,
          role: userEditForm.role,
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedUser.id);

      if (profileError) throw profileError;

      const { error: removeError } = await supabase
        .from('team_members_a1b2c3d4e5')
        .delete()
        .eq('user_id', selectedUser.id);

      if (removeError) throw removeError;

      if (userEditForm.teamIds.length > 0) {
        const memberships = userEditForm.teamIds.map(teamId => ({
          team_id: teamId,
          user_id: selectedUser.id
        }));

        const { error: membershipError } = await supabase
          .from('team_members_a1b2c3d4e5')
          .insert(memberships);

        if (membershipError) throw membershipError;
      }

      const { data: updatedUsers, error: usersError } = await supabase
        .from('user_profiles_a1b2c3d4e5')
        .select('*')
        .order('full_name');

      if (usersError) throw usersError;
      setAllUsers(updatedUsers || []);

      const updatedUser = updatedUsers?.find(u => u.id === selectedUser.id);
      if (updatedUser) {
        setSelectedUser(updatedUser);
      }

      if (selectedUser.id === user.id) {
        console.log('Updated own role, refreshing profile context...');
        await refreshUserProfile();
      }

      fetchData();

      toast.success(t('teamManagement.userUpdated', 'User updated successfully'));
      setIsEditingUser(false);
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error(t('teamManagement.updateUserError', 'Error updating user'));
    } finally {
      setUserUpdateLoading(false);
    }
  };

  const handleCreateTeam = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const { data: team, error: teamError } = await supabase
        .from('teams_a1b2c3d4e5')
        .insert([
          {
            name: teamForm.name,
            description: teamForm.description
          }
        ])
        .select();

      if (teamError) throw teamError;

      if (team && team.length > 0) {
        if (teamForm.allowedAgents.length > 0) {
          const agentRecords = teamForm.allowedAgents.map(agentId => ({
            team_id: team[0].id,
            agent_id: agentId
          }));

          const { error: agentsError } = await supabase
            .from('team_allowed_agents_a1b2c3d4e5')
            .insert(agentRecords);

          if (agentsError) throw agentsError;
        }

        const { error: memberError } = await supabase
          .from('team_members_a1b2c3d4e5')
          .insert([
            {
              team_id: team[0].id,
              user_id: user.id
            }
          ]);

        if (memberError) throw memberError;

        const { data: teamsData } = await supabase
          .from('teams_a1b2c3d4e5')
          .select('*');

        setTeams(teamsData || []);
        toast.success(t('teamManagement.teamCreated', 'Team created successfully'));
        setIsCreatingTeam(false);
        setTeamForm({ name: '', description: '', allowedAgents: [] });
      }
    } catch (error) {
      console.error('Error creating team:', error);
      toast.error(t('teamManagement.createTeamError', 'Error creating team'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateTeam = async (e) => {
    e.preventDefault();
    if (!selectedTeam) return;

    setIsLoading(true);

    try {
      const { error: teamError } = await supabase
        .from('teams_a1b2c3d4e5')
        .update({
          name: teamForm.name,
          description: teamForm.description
        })
        .eq('id', selectedTeam.id);

      if (teamError) throw teamError;

      const { error: deleteError } = await supabase
        .from('team_allowed_agents_a1b2c3d4e5')
        .delete()
        .eq('team_id', selectedTeam.id);

      if (deleteError) throw deleteError;

      if (teamForm.allowedAgents.length > 0) {
        const agentRecords = teamForm.allowedAgents.map(agentId => ({
          team_id: selectedTeam.id,
          agent_id: agentId
        }));

        const { error: agentsError } = await supabase
          .from('team_allowed_agents_a1b2c3d4e5')
          .insert(agentRecords);

        if (agentsError) throw agentsError;
      }

      const { data: teamsData } = await supabase
        .from('teams_a1b2c3d4e5')
        .select('*');

      setTeams(teamsData || []);
      setSelectedTeam(null);      
      toast.success(t('teamManagement.teamUpdated', 'Team updated successfully'));
    } catch (error) {
      console.error('Error updating team:', error);
      toast.error(t('teamManagement.updateTeamError', 'Error updating team'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteTeam = async (teamId) => {
    if (!confirm(t('teamManagement.deleteTeamConfirm', 'Are you sure you want to delete this team? This action cannot be undone.'))) {
      return;
    }

    setIsLoading(true);

    try {
      const { error } = await supabase
        .from('teams_a1b2c3d4e5')
        .delete()
        .eq('id', teamId);

      if (error) throw error;

      const { data: teamsData } = await supabase
        .from('teams_a1b2c3d4e5')
        .select('*');

      setTeams(teamsData || []);
      if (selectedTeam?.id === teamId) {
        setSelectedTeam(null);
      }

      toast.success(t('teamManagement.teamDeleted', 'Team deleted successfully'));
    } catch (error) {
      console.error('Error deleting team:', error);
      toast.error(t('teamManagement.deleteTeamError', 'Error deleting team'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleInviteUser = async (e) => {
    e.preventDefault();
    if (!inviteForm.fullName.trim() || !inviteForm.email || inviteForm.teamIds.length === 0) {
      toast.error(t('teamManagement.inviteFormError', 'Full name, email, and at least one team are required'));
      return;
    }

    setInvitationLoading(true);
    
    try {
      console.log('🔄 Starting invitation process for:', inviteForm.email);
      const uniqueInvitationId = await generateUniqueInvitationId();
      console.log('✅ Generated unique invitation ID:', uniqueInvitationId);

      const selectedTeamNames = teams
        .filter(team => inviteForm.teamIds.includes(team.id))
        .map(team => team.name);

      const webhookPayload = {
        fullName: inviteForm.fullName.trim(),
        email: inviteForm.email.trim().toLowerCase(),
        role: inviteForm.role,
        invitationId: uniqueInvitationId,
        teamIds: inviteForm.teamIds,
        teamNames: selectedTeamNames,
        invitedBy: user.fullName,
        invitedByEmail: user.email,
        invitedById: user.id,
        timestamp: new Date().toISOString(),
        source: 'team-management-app',
        appUrl: window.location.origin,
        supabaseUrl: 'https://yswjmkygdqjrzgwqeuse.supabase.co'
      };

      console.log('📤 Sending invitation payload to webhook:', {
        fullName: webhookPayload.fullName,
        email: webhookPayload.email,
        invitationId: webhookPayload.invitationId,
        teamCount: webhookPayload.teamIds.length,
        teamNames: webhookPayload.teamNames
      });

      const response = await fetch('https://prosomoinc.app.n8n.cloud/webhook/b1c39304-4d69-4e5c-8db1-089341eae38e', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(webhookPayload)
      });

      console.log('📨 Webhook response status:', response.status);
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Webhook error response:', errorText);
        throw new Error(`Invitation service failed with status ${response.status}: ${errorText}`);
      }

      let result;
      try {
        result = await response.json();
        console.log('✅ Webhook response data:', result);
      } catch (parseError) {
        console.warn('⚠️ Could not parse webhook response as JSON, treating as success');
        result = { success: true, message: t('teamManagement.invitationSent', 'Invitation sent successfully') };
      }

      if (result.success === false) {
        throw new Error(result.message || result.error || t('teamManagement.invitationFailed', 'Invitation service reported failure'));
      }

      const successMessage = t(
        'teamManagement.invitationSuccess',
        `Invitation sent successfully to {fullName} ({email})! The user will receive an email with:
• Login instructions for the platform
• Access to {teamCount} team{teamPlural}: {teamNames}
• Role assignment: {role}
The user account will be created automatically when they accept the invitation.`)
       .replace('{fullName}', inviteForm.fullName)
       .replace('{email}', inviteForm.email)
       .replace('{teamCount}', selectedTeamNames.length)
       .replace('{teamPlural}', selectedTeamNames.length > 1 ? 's' : '')
       .replace('{teamNames}', selectedTeamNames.join(','))
       .replace('{role}', inviteForm.role);

      toast.success(successMessage);

      setIsInvitingUser(false);
      setInviteForm({
        fullName: '',
        email: '',
        role: 'Team Member',
        teamIds: []
      });

      console.log('✅ Invitation process completed successfully');
    } catch (error) {
      console.error('❌ Error in invitation process:', error);

      let errorMessage = t('teamManagement.invitationError', 'Failed to send invitation. ');
      if (error.message.includes('Failed to generate unique invitation ID')) {
        errorMessage += t('teamManagement.invitationIdErrorDetails', 'Could not generate a unique invitation ID. Please try again.');
      } else if (error.message.includes('fetch')) {
        errorMessage += t('teamManagement.networkError', 'Network error occurred. Please check your connection and try again.');
      } else if (error.message.includes('status')) {
        errorMessage += t('teamManagement.serviceUnavailable', 'The invitation service is temporarily unavailable. Please try again later.');
      } else {
        errorMessage += error.message || t('teamManagement.unknownError', 'Unknown error occurred.');
      }

      toast.error(errorMessage);
    } finally {
      setInvitationLoading(false);
    }
  };

  const handleAddToTeam = async (userId) => {
    if (!selectedTeam) return;
    
    try {
      setIsLoading(true);
      
      const { data: existingMembership, error: checkError } = await supabase
        .from('team_members_a1b2c3d4e5')
        .select('*')
        .eq('team_id', selectedTeam.id)
        .eq('user_id', userId)
        .maybeSingle();
      
      if (checkError && checkError.code !== 'PGRST116') throw checkError;
      
      if (existingMembership) {
        toast.info(t('teamManagement.userAlreadyInTeam', 'User is already a member of this team'));
        return;
      }
      
      const { error } = await supabase
        .from('team_members_a1b2c3d4e5')
        .insert([
          {
            team_id: selectedTeam.id,
            user_id: userId
          }
        ]);
      
      if (error) throw error;

      toast.success(t('teamManagement.userAddedToTeam', 'User added to team successfully'));
      setIsAddingMember(false);
      setSelectedTeam(null);
    } catch (error) {
      console.error('Error adding user to team:', error);
      toast.error(t('teamManagement.addUserToTeamError', 'Error adding user to team'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveFromTeam = async (teamId, userId) => {
    if (!confirm(t('teamManagement.removeUserConfirm', 'Are you sure you want to remove this user from the team?'))) {
      return;
    }

    try {
      setIsLoading(true);
      const { error } = await supabase
        .from('team_members_a1b2c3d4e5')
        .delete()
        .eq('team_id', teamId)
        .eq('user_id', userId);

      if (error) throw error;

      if (selectedTeam?.id === teamId) {
        handleSelectTeam(selectedTeam);
      }

      toast.success(t('teamManagement.userRemovedFromTeam', 'User removed from team successfully'));
    } catch (error) {
      console.error('Error removing user from team:', error);
      toast.error(t('teamManagement.removeUserError', 'Error removing user from team'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleAgentCheckbox = (agentId) => {
    setTeamForm(prev => {
      const allowedAgents = [...prev.allowedAgents];
      if (allowedAgents.includes(agentId)) {
        return { ...prev, allowedAgents: allowedAgents.filter(id => id !== agentId) };
      } else {
        return { ...prev, allowedAgents: [...allowedAgents, agentId] };
      }
    });
  };

  const handleTeamCheckbox = (teamId) => {
    setInviteForm(prev => {
      const teamIds = [...prev.teamIds];
      if (teamIds.includes(teamId)) {
        return { ...prev, teamIds: teamIds.filter(id => id !== teamId) };
      } else {
        return { ...prev, teamIds: [...teamIds, teamId] };
      }
    });
  };

  const handleUserTeamCheckbox = (teamId) => {
    setUserEditForm(prev => {
      const teamIds = [...prev.teamIds];
      if (teamIds.includes(teamId)) {
        return { ...prev, teamIds: teamIds.filter(id => id !== teamId) };
      } else {
        return { ...prev, teamIds: [...teamIds, teamId] };
      }
    });
  };

  const filteredUsers = allUsers.filter(u => 
    (u.full_name?.toLowerCase().includes(userSearchQuery.toLowerCase()) || 
     u.email?.toLowerCase().includes(userSearchQuery.toLowerCase())) &&
    !teamMemberIds.includes(u.id)
  );

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center h-full p-6">
        <div className="text-center max-w-md px-4">
          <div className="text-red-500 text-6xl mb-4">🔒</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">{t('teamManagement.accessDenied', 'Access Denied')}</h2>
          <p className="text-gray-600 mb-4">
            {t('teamManagement.accessDeniedMessage', 'You don\'t have permission to access the Team Management page. Only users with the Administrator role can access this page.')}
          </p>
          <p className="text-sm text-gray-500 mb-6">
            {t('teamManagement.currentRole', 'Current role:')} <span className="font-mono bg-gray-100 px-2 py-1 rounded-xs">{user?.role || t('teamManagement.unknownRole', 'Unknown')}</span>
          </p>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={refreshUserProfile}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
          >
            <SafeIcon icon={FiRefreshCw} className="w-5 h-5" />
            <span>{t('teamManagement.refreshPermissions', 'Refresh Permissions')}</span>
          </motion.button>
        </div>
      </div>
    );
  }

  return (
      <div className="p-6 h-full overflow-y-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-12xl mx-auto"
        >
          <div className="mb-8 flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2 dark:text-white">
                {t('teamManagement.title', 'Team Management')}
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                {t('teamManagement.subtitle', 'Create a new team or select an existing one to manage members and settings. As an administrator, you can also manage all users and their roles.')}
              </p>
            </div>
            <div className="flex justify-center gap-3">
              <Button
                onClick={() => {
                  setTeamForm({ name: '', description: '', allowedAgents: [] });
                  setIsCreatingTeam(true);
                }}
                className="inline-flex items-center lg:space-x-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
                icon={FiPlus}
                responsive
              >
                {t('teamManagement.createNewTeam', 'Create New Team')}
              </Button>
              <Button
                onClick={() => setIsInvitingUser(true)}
                className="inline-flex items-center lg:space-x-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
                icon={FiUserPlus}
                responsive
              >
                {t('teamManagement.inviteUser', 'Invite User')}
              </Button>
            </div>            
          </div>

          <div>
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xs border border-gray-200 dark:border-gray-700 p-6 max-h-[calc(100vh-180px)] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{t('teamManagement.teams', 'Teams')}</h2>                
              </div>

              {isLoading && teams.length === 0 ? (
                <div className="py-8 text-center">
                  <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                  <p className="text-gray-500 dark:text-gray-400">{t('teamManagement.loadingTeams', 'Loading teams...')}</p>
                </div>
              ) : teams.length === 0 ? (
                <div className="py-8 text-center">
                  <p className="text-gray-500 dark:text-gray-400">{t('teamManagement.noTeams', 'No teams created yet')}</p>
                </div>
              ) : (
                <ul className="space-y-2">
                  {teams.map(team => (
                    <li key={team.id}>
                      <div
                        className={`w-full text-left px-4 py-3 rounded-lg transition-colors flex justify-between items-center ${
                          selectedTeam?.id === team.id ? 'bg-primary-50 border border-primary-200' : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}
                      >
                        <div>
                          <div className="font-medium text-gray-900 dark:text-white">{team.name}</div>
                          {team.description && (
                            <div className="text-sm text-gray-500 dark:text-gray-400">{team.description}</div>
                          )}
                        </div>
                        <div className="flex space-x-2">
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSelectTeam(team);
                              setSelectedUser(null);
                              setIsEditingUser(false);
                            }}
                            className="p-1 text-gray-400 hover:text-blue-500"
                            icon={FiEdit2}
                            title={t('teamManagement.editTeam', 'Edit Team')}
                          />
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              setIsAddingMember(true);
                              setSelectedTeam(team);
                            }}
                            className="p-1 text-gray-400 hover:text-blue-500"
                            icon={FiUserPlus}
                            title={t('teamManagement.addMember', 'Add Member')}
                          />
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteTeam(team.id);
                            }}
                            className="p-1 text-gray-400 hover:text-red-500"
                            icon={FiTrash2}
                            title={t('teamManagement.deleteTeam', 'Delete Team')}
                          />
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <Modal isOpen={isCreatingTeam} title={t('teamManagement.createTeamTitle', 'Create New Team')} onClose={() => setIsCreatingTeam(false)}>
              <form onSubmit={handleCreateTeam}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-400 mb-1">
                      {t('teamManagement.teamName', 'Team Name')}
                    </label>
                    <input
                      type="text"
                      value={teamForm.name}
                      onChange={(e) => setTeamForm({ ...teamForm, name: e.target.value })}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-400 mb-1">
                      {t('teamManagement.description', 'Description')}
                    </label>
                    <textarea
                      value={teamForm.description}
                      onChange={(e) => setTeamForm({ ...teamForm, description: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      rows="3"
                    ></textarea>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-400 mb-2">
                      {t('teamManagement.allowedAgents', 'Allowed AI Agents')}
                    </label>
                    <div className="space-y-2 max-h-60 overflow-y-auto p-2 border border-gray-200 rounded-lg">
                      {availableAgents.map(agent => (
                        <label
                          key={agent.id}
                          className="flex items-center space-x-3 p-2 hover:bg-gray-50 rounded-lg"
                        >
                          <input
                            type="checkbox"
                            checked={teamForm.allowedAgents.includes(agent.id)}
                            onChange={() => handleAgentCheckbox(agent.id)}
                            className="text-primary-500 focus:ring-primary-500"
                          />
                          <div className="flex items-center space-x-2">
                            <div
                              className={`w-8 h-8 rounded-lg ${agent.color} flex items-center justify-center text-white`}
                            >
                              {agent.icon}
                            </div>
                            <span>{agent.name}</span>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="mt-6 flex justify-end space-x-3">
                  <Button
                    onClick={() => {
                      setIsCreatingTeam(false);
                    }}
                    className="px-4 py-2 border border-gray-300 text-gray-700 dark:text-gray-400 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    {t('teamManagement.cancel', 'Cancel')}
                  </Button>                    
                  <Button
                    type="submit"
                    disabled={isLoading}
                    className="flex items-center space-x-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 focus:outline-hidden focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    icon={FiSave}
                    loading={isLoading}
                  >
                    {t('teamManagement.createTeam', 'Create Team')}
                  </Button>
                </div>
              </form>
            </Modal>
            <Modal isOpen={isInvitingUser} title={t('teamManagement.inviteUserTitle', 'Invite a new user')} onClose={() => setIsInvitingUser(false)}>
              <form onSubmit={handleInviteUser}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-400 mb-1">
                      {t('teamManagement.fullName', 'Full Name *')}
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={inviteForm.fullName}
                        onChange={(e) => setInviteForm({ ...inviteForm, fullName: e.target.value })}
                        required
                        className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder={t('teamManagement.fullNamePlaceholder', 'John Doe')}
                      />
                      <SafeIcon
                        icon={FiUser}
                        className="absolute left-3 top-2.5 w-5 h-5 text-gray-400"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-400 mb-1">
                      {t('teamManagement.emailAddress', 'Email Address *')}
                    </label>
                    <div className="relative">
                      <input
                        type="email"
                        value={inviteForm.email}
                        onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                        required
                        className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder={t('teamManagement.emailPlaceholder', 'john.doe@example.com')}
                      />
                      <SafeIcon
                        icon={FiMail}
                        className="absolute left-3 top-2.5 w-5 h-5 text-gray-400"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-400 mb-1">
                      {t('teamManagement.role', 'Role')}
                    </label>
                    <select
                      value={inviteForm.role}
                      onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      <option value="Administrator">{t('teamManagement.administrator', 'Administrator')}</option>
                      <option value="Team Member">{t('teamManagement.teamMember', 'Team Member')}</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-400 mb-2">
                      {t('teamManagement.assignToTeams', 'Assign to Teams * (Select at least one)')}
                    </label>
                    <div className="space-y-2 max-h-60 overflow-y-auto p-2 border border-gray-200 rounded-lg">
                      {teams.map(team => (
                        <label
                          key={team.id}
                          className="flex items-center space-x-3 p-2 hover:bg-gray-50 rounded-lg"
                        >
                          <input
                            type="checkbox"
                            checked={inviteForm.teamIds.includes(team.id)}
                            onChange={() => handleTeamCheckbox(team.id)}
                            className="text-primary-500 focus:ring-primary-500"
                          />
                          <span className="font-medium">{team.name}</span>
                          {team.description && (
                            <span className="text-sm text-gray-500">{team.description}</span>
                          )}
                        </label>
                      ))}
                    </div>
                    {teams.length === 0 && (
                      <p className="text-sm text-red-500 mt-1">
                        {t('teamManagement.noTeamsForInvite', '⚠️ You need to create at least one team before inviting users')}
                      </p>
                    )}
                  </div>
                </div>
                <div className="mt-6 flex justify-end space-x-3">
                  <Button
                    onClick={() => {
                      setIsInvitingUser(false);
                    }}
                    className="px-4 py-2 border border-gray-300 text-gray-700 dark:text-gray-400 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    {t('teamManagement.cancel', 'Cancel')}
                  </Button>                          
                  <Button
                    type="submit"
                    disabled={
                      invitationLoading ||
                      inviteForm.teamIds.length === 0 ||
                      !inviteForm.email.trim() ||
                      !inviteForm.fullName.trim()
                    }
                    loading={invitationLoading}
                    icon={FiUserPlus}
                  >
                    {t('teamManagement.sendInvitation', 'Send Invitation')}                    
                  </Button>
                </div>
              </form>
            </Modal>
            {selectedUser && (
              <Modal isOpen={isEditingUser} title={t('teamManagement.editUserTitle', 'Edit User: {name}').replace('{name}',selectedUser.full_name)} onClose={() => setIsEditingUser(false)}>
                <form onSubmit={handleUpdateUser}>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-400 mb-1">
                        {t('teamManagement.fullName', 'Full Name *')}
                      </label>
                      <input
                        type="text"
                        value={userEditForm.fullName}
                        onChange={(e) =>
                          setUserEditForm({ ...userEditForm, fullName: e.target.value })
                        }
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>                      
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-400 mb-1">
                        {t('teamManagement.role', 'Role')}
                      </label>
                      <select
                        value={userEditForm.role}
                        onChange={(e) => setUserEditForm({ ...userEditForm, role: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700"
                      >
                        <option value="Administrator">{t('teamManagement.administrator', 'Administrator')}</option>
                        <option value="Team Member">{t('teamManagement.teamMember', 'Team Member')}</option>
                      </select>
                      {selectedUser.id === user.id && userEditForm.role !== selectedUser.role && (
                        <p className="text-xs text-yellow-500 mt-1">
                          {t('teamManagement.roleChangeWarning', '⚠️ Changing your own role may affect your access to certain features')}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-400 mb-2">
                        {t('teamManagement.teamMemberships', 'Team Memberships')}
                      </label>
                      <div className="space-y-2 max-h-60 overflow-y-auto p-2 border border-gray-200 rounded-lg">
                        {teams.map(team => (
                          <label
                            key={team.id}
                            className="flex items-center space-x-3 p-2 hover:bg-gray-50 rounded-lg"
                          >
                            <input
                              type="checkbox"
                              checked={userEditForm.teamIds.includes(team.id)}
                              onChange={() => handleUserTeamCheckbox(team.id)}
                              className="text-primary-500 focus:ring-primary-500"
                            />
                            <span className="font-medium">{team.name}</span>
                            {team.description && (
                              <span className="text-sm text-gray-500">{team.description}</span>
                            )}
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="mt-6 flex justify-end space-x-3">
                    <Button
                      onClick={() => {
                        setIsEditingUser(false);
                        setSelectedUser(null);
                      }}
                      className="px-4 py-2 border border-gray-300 text-gray-700 dark:text-gray-400 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      {t('teamManagement.cancel', 'Cancel')}
                    </Button>
                    <Button
                      type="submit"
                      disabled={userUpdateLoading}
                      loading={userUpdateLoading}
                      icon={FiSave}
                    >
                      {t('teamManagement.updateUser', 'Update User')}
                    </Button>
                  </div>
                </form>
              </Modal>
            )}
            {selectedTeam && isAddingMember && (
              <Modal isOpen={isAddingMember} title={t('teamManagement.addMembersTitle', 'Add Members to {name}').replace('{name}', selectedTeam.name)}  onClose={() => {
                setIsAddingMember(false)
                setSelectedTeam(null)
              }}>
                <div className="mb-4">
                  <div className="relative">
                    <input
                      type="text"
                      value={userSearchQuery}
                      onChange={(e) => setUserSearchQuery(e.target.value)}
                      placeholder={t('teamManagement.searchUsersPlaceholder', 'Search users by name or email...')}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500"
                    />
                    <SafeIcon
                      icon={FiSearch}
                      className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"
                    />
                  </div>
                </div>
                <div className="max-h-[400px] overflow-y-auto">
                  {filteredUsers.length === 0 ? (
                    <div className="p-4 text-center text-gray-500">
                      {t('teamManagement.noUsersFound', 'No users found matching your search or all users are already in this team')}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {filteredUsers.map(user => (
                        <div
                          key={user.id}
                          className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 hover:dark:bg-gray-500"
                        >
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 rounded-full overflow-hidden bg-gray-200">
                              <img
                                src={user.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.full_name || 'User')}&background=0ea5e9&color=fff`}
                                alt={t('teamManagement.userAvatarAlt', '{name}').replace('{name}')}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(user.full_name || 'User')}&background=0ea5e9&color=fff`;
                                }}
                              />
                            </div>
                            <div>
                              <div className="font-medium text-gray-900 dark:text-white">{user.full_name}</div>
                            </div>
                          </div>
                          <Button
                            onClick={() => handleAddToTeam(user.id)}
                            icon={FiUserPlus}
                            responsive
                          >
                            {t('teamManagement.addToTeam', 'Add to Team')}
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </Modal>
            )}
            {selectedTeam && !isAddingMember && (
              <Modal isOpen={true} title={t('teamManagement.manageTeamTitle', 'Manage Team: {name}').replace('{name}',selectedTeam.name)}  onClose={() => setSelectedTeam(null)}>
                <form onSubmit={handleUpdateTeam}>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-400 mb-1">
                        {t('teamManagement.teamName', 'Team Name')}
                      </label>
                      <input
                        type="text"
                        value={teamForm.name}
                        onChange={(e) => setTeamForm({ ...teamForm, name: e.target.value })}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-400 mb-1">
                        {t('teamManagement.description', 'Description')}
                      </label>
                      <textarea
                        value={teamForm.description}
                        onChange={(e) => setTeamForm({ ...teamForm, description: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        rows="3"
                      ></textarea>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-400 mb-2">
                        {t('teamManagement.allowedAgents', 'Allowed AI Agents')}
                      </label>
                      <div className="space-y-2 max-h-60 overflow-y-auto p-2 border border-gray-200 rounded-lg">
                        {availableAgents.map(agent => (
                          <label
                            key={agent.id}
                            className="flex items-center space-x-3 p-2 hover:bg-gray-50 rounded-lg dark:hover:bg-gray-700"
                          >
                            <input
                              type="checkbox"
                              checked={teamForm.allowedAgents.includes(agent.id)}
                              onChange={() => handleAgentCheckbox(agent.id)}
                              className="text-primary-500 focus:ring-primary-500"
                            />
                            <div className="flex items-center space-x-2">
                              <div
                                className={`w-8 h-8 rounded-lg ${agent.color} flex items-center justify-center text-white`}
                              >
                                {agent.icon}
                              </div>
                              <span>{agent.name}</span>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                    <div className="mt-6 flex justify-end">
                      <Button
                        type="submit"
                        disabled={isLoading}
                        className="flex items-center space-x-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 focus:outline-hidden focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        loading={isLoading}
                        icon={FiSave}
                      >
                        {t('teamManagement.updateTeam', 'Update Team')}
                      </Button>
                    </div>
                  </div>
                </form>
                <div className="mt-8 border-t border-gray-200 pt-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-400 mb-4">{t('teamManagement.teamMembers', 'Team Members')}</h3>
                  <TeamMembersList teamId={selectedTeam.id} onRemoveMember={handleRemoveFromTeam} />
                </div>
              </Modal>
            )}
          </div>

          {isAdmin && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="mt-8 bg-white dark:bg-gray-800 rounded-xl shadow-xs border border-gray-200 dark:border-gray-700"
            >
              <div className="flex items-center justify-between mb-6 p-6 pb-0">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
                    <SafeIcon icon={FiShield} className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{t('teamManagement.allUsers', 'All Users')}</h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{t('teamManagement.allUsersDescription', 'Manage all users in the system')}</p>
                  </div>
                </div>
                <Button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    setIsInvitingUser(true)
                  }}
                  icon={FiUserPlus}
                  responsive
                >
                  {t('teamManagement.inviteNewUser', 'Invite New User')}
                </Button>
              </div>
              <div className="overflow-x-scroll">
                <table className="table-auto w-full">
                  <thead className="bg-gray-50 dark:bg-gray-900">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium dark:text-gray-400 uppercase tracking-wider">{t('teamManagement.actions', 'Actions')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium dark:text-gray-400 uppercase tracking-wider">{t('teamManagement.user', 'User')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium dark:text-gray-400 uppercase tracking-wider">{t('teamManagement.role', 'Role')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium dark:text-gray-400 uppercase tracking-wider">{t('teamManagement.teams', 'Teams')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium dark:text-gray-400 uppercase tracking-wider">{t('teamManagement.created', 'Created')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {allUsers.map(userItem => (
                      <UserRow
                        key={userItem.id}
                        user={userItem}
                        teams={teams}
                        onSelectUser={handleSelectUser}
                        isSelected={selectedUser?.id === userItem.id}
                        setIsEditingUser={setIsEditingUser}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>
          )}
        </motion.div>
      </div>
  );
};

const UserRow = ({ user, teams, onSelectUser, isSelected, setIsEditingUser }) => {
  const t = useTranslation(user?.language || 'fr'); // Default to French
  const [userTeams, setUserTeams] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUserTeams = async () => {
      try {
        const { data: memberships, error } = await supabase
          .from('team_members_a1b2c3d4e5')
          .select('team_id')
          .eq('user_id', user.id);

        if (error) throw error;

        const userTeamIds = memberships?.map(m => m.team_id) || [];
        const userTeamNames = teams
          .filter(t => userTeamIds.includes(t.id))
          .map(t => t.name);

        setUserTeams(userTeamNames);
      } catch (error) {
        console.error('Error fetching user teams:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserTeams();
  }, [user.id, teams]);

  return (
    <tr
      className={`hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors ${isSelected ? 'bg-blue-50 dark:bg-gray-700' : ''}`}
      onClick={() => onSelectUser(user)}
    >      
      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
        <Button
          onClick={(e) => {
            e.stopPropagation();
            onSelectUser(user);
            setIsEditingUser(true);
          }}
          title={t('teamManagement.editUser', 'Edit User')}
          className="text-gray-400 hover:text-primary-400"
          icon={FiEdit2}
        />
      </td>           
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center">
          <div className="w-10 h-10 rounded-full overflow-hidden bg-gray-200 shrink-0">
            <img
              src={user.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.full_name || 'User')}&background=0ea5e9&color=fff`}
              alt={t('teamManagement.userAvatarAlt', '{name}').replace('{name}')}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(user.full_name || 'User')}&background=0ea5e9&color=fff`;
              }}
            />
          </div>
          <div className="ml-4">
            <div className="text-sm font-medium text-gray-900 dark:text-white">{user.full_name}</div>
            <div className="text-sm text-gray-500 dark:text-gray-400">{user.email}</div>
          </div>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <span
          className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
            user.role === 'Administrator' ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-800'
          }`}
        >
          {user.role}
        </span>
      </td>
      <td className="px-6 py-4">
        {loading ? (
          <div className="text-sm text-gray-500">{t('teamManagement.loading', 'Loading...')}</div>
        ) : userTeams.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {userTeams.map((teamName, index) => (
              <span
                key={index}
                className="inline-flex px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full"
              >
                {teamName}
              </span>
            ))}
          </div>
        ) : (
          <span className="text-sm text-gray-400">{t('teamManagement.noTeamsAssigned', 'No teams')}</span>
        )}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
        {user.created_at ? new Date(user.created_at).toLocaleDateString() : t('teamManagement.na', 'N/A')}
      </td>
    </tr>
  );
};

const UserDetailsView = ({ user, teams }) => {
  const t = useTranslation(user?.language || 'fr'); // Default to French
  const [userTeams, setUserTeams] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUserTeams = async () => {
      try {
        const { data: memberships, error } = await supabase
          .from('team_members_a1b2c3d4e5')
          .select('team_id')
          .eq('user_id', user.id);

        if (error) throw error;

        const userTeamIds = memberships?.map(m => m.team_id) || [];
        const userTeamObjects = teams.filter(t => userTeamIds.includes(t.id));

        setUserTeams(userTeamObjects);
      } catch (error) {
        console.error('Error fetching user teams:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserTeams();
  }, [user.id, teams]);

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-4">
        <div className="w-16 h-16 rounded-full overflow-hidden bg-gray-200">
          <img
            src={user.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.full_name || 'User')}&background=0ea5e9&color=fff`}
            alt={t('teamManagement.userAvatarAlt', '{name}').replace('{name}')}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(user.full_name || 'User')}&background=0ea5e9&color=fff`;
            }}
          />
        </div>
        <div>
          <h3 className="text-xl font-semibold text-gray-900">{user.full_name}</h3>
          <p className="text-gray-600">{user.email}</p>
          <span
            className={`inline-flex px-2 py-1 text-xs font-medium rounded-full mt-1 ${
              user.role === 'Administrator' ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-800'
            }`}
          >
            {user.role}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <h4 className="text-lg font-medium text-gray-900 mb-3">{t('teamManagement.accountInformation', 'Account Information')}</h4>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-gray-500">{t('teamManagement.fullName', 'Full Name')}</label>
              <p className="text-gray-900">{user.full_name}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">{t('teamManagement.email', 'Email')}</label>
              <p className="text-gray-900">{user.email}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">{t('teamManagement.role', 'Role')}</label>
              <p className="text-gray-900">{user.role}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">{t('teamManagement.language', 'Language')}</label>
              <p className="text-gray-900">{user.language === 'fr' ? t('teamManagement.french', 'French') : t('teamManagement.english', 'English')}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">{t('teamManagement.created', 'Created')}</label>
              <p className="text-gray-900">
                {user.created_at ? new Date(user.created_at).toLocaleDateString() : t('teamManagement.na', 'N/A')}
              </p>
            </div>
          </div>
        </div>

        <div>
          <h4 className="text-lg font-medium text-gray-900 mb-3">{t('teamManagement.teamMemberships', 'Team Memberships')}</h4>
          {loading ? (
            <div className="text-sm text-gray-500">{t('teamManagement.loading', 'Loading...')}</div>
          ) : userTeams.length > 0 ? (
            <div className="space-y-2">
              {userTeams.map(team => (
                <div key={team.id} className="p-3 bg-gray-50 rounded-lg">
                  <div className="font-medium text-gray-900">{team.name}</div>
                  {team.description && (
                    <div className="text-sm text-gray-500">{team.description}</div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500">{t('teamManagement.noTeamsAssigned', 'Not a member of any teams')}</p>
          )}
        </div>
      </div>
    </div>
  );
};

const TeamMembersList = ({ teamId, onRemoveMember }) => {
  const [members, setMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const { user } = useAuth();
  const t = useTranslation(user?.language);

  const fetchMembers = async () => {
    setIsLoading(true);
    try {
      const { data: memberships, error: membershipError } = await supabase
        .from('team_members_a1b2c3d4e5')
        .select('user_id')
        .eq('team_id', teamId);

      if (membershipError) throw membershipError;

      if (memberships && memberships.length > 0) {
        const userIds = memberships.map(m => m.user_id);

        const { data: profiles, error: profilesError } = await supabase
          .from('user_profiles_a1b2c3d4e5')
          .select('*')
          .in('id', userIds);

        if (profilesError) throw profilesError;
        setMembers(profiles || []);
      } else {
        setMembers([]);
      }
    } catch (error) {
      console.error('Error fetching team members:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchMembers();
  }, [teamId]);

  if (isLoading) {
    return (
      <div className="py-8 text-center">
        <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
        <p className="text-gray-500">{t('teamManagement.loadingTeamMembers', 'Loading team members...')}</p>
      </div>
    );
  }

  if (members.length === 0) {
    return (
      <div className="py-8 text-center">
        <p className="text-gray-500">{t('teamManagement.noMembers', 'No members in this team')}</p>
        <p className="text-sm text-gray-400 mt-1">{t('teamManagement.invitePrompt', 'Use the invite button above to add users')}</p>
      </div>
    );
  }

  return (
    <ul className="space-y-2 max-h-64 overflow-y-auto">
      {members.map(member => (
        <li key={member.id} className="flex items-center justify-between p-3 border border-gray-100 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-full overflow-hidden bg-gray-200">
              <img
                src={member.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(member.full_name || 'User')}&background=0ea5e9&color=fff`}
                alt={t('teamManagement.memberAvatarAlt', '{name}').replace('{name}')}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(member.full_name || 'User')}&background=0ea5e9&color=fff`;
                }}
              />
            </div>
            <div>
              <div className="font-medium text-gray-900 dark:text-gray-400">{member.full_name}</div>
              <div className="text-sm text-gray-500 dark:text-gray-400">{member.role}</div>
            </div>
          </div>
          <Button
            onClick={() => onRemoveMember(teamId, member.id).then(fetchMembers)}
            className="text-gray-400 hover:text-red-500 hover:bg-red-50"
            title={t('teamManagement.removeFromTeam', 'Remove from team')}
            icon={FiUserX}
          />
        </li>
      ))}
    </ul>
  );
};

export default TeamManagementPage;